import type { ConfidenceModel, ForecastModel } from '@/shared/generated'
import type { TFunction } from 'i18next'

import { AlertTriangleIcon, InfoIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
	buildFanChartData,
	normalizeReasons,
} from '@/entities/mid-term-prediction'

import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
} from '@/shared/components/ui/empty'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'

import { formatPeriod, getForecastWeeks } from '../lib/period'
import { ForecastFanChart } from './ForecastFanChart'
import { NotForecastableBanner } from './NotForecastableBanner'

type ConfidenceBadge = {
	variant: 'success' | 'warning' | 'secondary'
	key: string
	defaultValue: string
}

const CONFIDENCE_BADGE: Record<ConfidenceModel['level'], ConfidenceBadge> = {
	high: {
		variant: 'success',
		key: 'midterm_forecast.confidence_high',
		defaultValue: 'High confidence',
	},
	medium: {
		variant: 'warning',
		key: 'midterm_forecast.confidence_medium',
		defaultValue: 'Medium confidence',
	},
	low: {
		variant: 'secondary',
		key: 'midterm_forecast.confidence_low',
		defaultValue: 'Low confidence',
	},
}

const UNKNOWN_CONFIDENCE_BADGE: ConfidenceBadge = {
	variant: 'secondary',
	key: 'midterm_forecast.confidence_unknown',
	defaultValue: 'Unknown confidence',
}

const getConfidenceBadge = (level: ConfidenceModel['level']): ConfidenceBadge =>
	CONFIDENCE_BADGE[level] ?? UNKNOWN_CONFIDENCE_BADGE

const ForecastErrorState = ({ t }: { t: TFunction }) => (
	<Empty data-testid='mtf-forecast-error'>
		<EmptyHeader>
			<EmptyMedia variant='icon' className='text-destructive'>
				<AlertTriangleIcon />
			</EmptyMedia>
			<EmptyDescription>
				{t('midterm_forecast.error', {
					defaultValue: 'Failed to load the forecast. Please try again later.',
				})}
			</EmptyDescription>
		</EmptyHeader>
	</Empty>
)

export const ForecastSection = ({
	forecast,
	isPending,
	isError,
}: {
	forecast: ForecastModel | undefined
	isPending: boolean
	isError: boolean
}) => {
	const { t } = useTranslation()

	if (isPending) {
		return (
			<div className='flex flex-col gap-3' data-testid='mtf-forecast-skeleton'>
				<Skeleton className='h-5 w-48' />
				<Skeleton className='h-64 w-full' />
			</div>
		)
	}

	if (isError || !forecast) {
		return <ForecastErrorState t={t} />
	}

	if (!forecast.is_forecastable) {
		return (
			<NotForecastableBanner
				reasons={normalizeReasons(forecast)}
				period={forecast.forecast_period}
			/>
		)
	}

	if (!forecast.sales_quantity) {
		return <ForecastErrorState t={t} />
	}

	const confidence = forecast.confidence
	const confidenceBadge = confidence && getConfidenceBadge(confidence.level)
	const total = forecast.sales_quantity_total
	const weeks = getForecastWeeks(forecast.forecast_period)
	const { points, todayDate } = buildFanChartData(forecast.sales_quantity)

	return (
		<section className='flex flex-col gap-3' data-testid='mtf-forecast-section'>
			<div className='flex items-start justify-between gap-3'>
				<div>
					<h3 className='inline-flex items-center gap-1.5 text-sm font-semibold'>
						{t('midterm_forecast.section_title', {
							defaultValue: '{weeks}-week baseline forecast',
							weeks,
						})}
						<Tooltip>
							<TooltipTrigger asChild>
								<Button
									variant='ghost'
									size='icon-sm'
									data-testid='mtf-forecast-info'
									aria-label={t('midterm_forecast.how_computed', {
										defaultValue: 'How this is computed',
									})}
								>
									<InfoIcon className='size-3.5 text-muted-foreground' />
								</Button>
							</TooltipTrigger>
							<TooltipContent className='max-w-72'>
								{t('midterm_forecast.range_tooltip', {
									defaultValue:
										'Sum of the q25–q75 daily scenarios — the central 50% of likely outcomes over the {weeks}-week window. The 90% range (q05–q95) shows on the chart bands.',
									weeks,
								})}
							</TooltipContent>
						</Tooltip>
					</h3>
					{forecast.forecast_period && (
						<p className='text-xs text-muted-foreground'>
							{formatPeriod(
								forecast.forecast_period.starts_on,
								forecast.forecast_period.ends_on
							)}
						</p>
					)}
				</div>
				{confidence && confidenceBadge && (
					<Tooltip>
						<TooltipTrigger asChild>
							<Badge
								variant={confidenceBadge.variant}
								data-testid='mtf-confidence-badge'
							>
								{t(confidenceBadge.key, {
									defaultValue: confidenceBadge.defaultValue,
								})}
							</Badge>
						</TooltipTrigger>
						<TooltipContent>
							{t('midterm_forecast.confidence_tooltip', {
								defaultValue:
									'Derived from forecast interval width — the narrower the q25–q75 band relative to the median, the higher the confidence.',
							})}
						</TooltipContent>
					</Tooltip>
				)}
			</div>
			<div className='rounded-xl border bg-card p-4'>
				{total && (
					<div className='flex flex-col items-end'>
						<span
							className='text-2xl font-extrabold tabular-nums'
							data-testid='mtf-kpi-total'
						>
							{t('midterm_forecast.kpi_units', {
								defaultValue: '{value} units',
								defaultValue_one: '{value} unit',
								count: total.total,
								value: formatNumber(total.total, NumberFormats.Integer),
							})}
						</span>
						<span className='text-xs text-muted-foreground tabular-nums'>
							{t('midterm_forecast.kpi_range', {
								defaultValue: 'range {lo} – {hi}',
								lo: formatNumber(total.lo, NumberFormats.Integer),
								hi: formatNumber(total.hi, NumberFormats.Integer),
							})}
						</span>
					</div>
				)}
				<ForecastFanChart points={points} todayDate={todayDate} />
			</div>
		</section>
	)
}
